// Post-quantum key wrapping (M78, docs/post-quantum.md D335–D340): each
// device's ML-KEM-768 key next to its P-256 key, and a chat or family key
// wrapped for a device with a key derived from both (wrapped key v2, D337).
// Plus the pure decisions of D338–D339 the shared vector
// (shared/contracts/pq-wrap-vector.json) holds every platform to: what to
// wrap for whom, what to learn, which v1 copies to refuse, when to rotate.
//
// ML-KEM is @noble/post-quantum 0.7.1, pinned exactly and in the audit's
// scope (#78); WebCrypto has no ML-KEM in browsers yet (§4.1, §4.4).
import { ml_kem768 } from '@noble/post-quantum/ml-kem.js';
import { PQ, wrappedKeyFormat } from '@twinden/shared';
import { fromBase64, toBase64 } from './crypto';
import { aadV2 } from './envelopeV2';

const subtle = () => globalThis.crypto.subtle;
const encoder = new TextEncoder();

export const MLKEM_SEED_BYTES = 64;
const IV_BYTES = 12;
const VERSION = 0x02;
const SALT = encoder.encode('twinden wrap v2');
const U32_MAX = 0xffffffff;

export { wrappedKeyFormat };

/** An ML-KEM-768 key pair, opened in memory (§4.4). */
export interface MlkemKeyPair {
  /** d ‖ z, FIPS 203 KeyGen_internal's input: what is kept, sealed. */
  seed: Uint8Array;
  /** The 1184-byte encapsulation key `ek`, base64: what is published. */
  publicKey: string;
  /** The 2400-byte decapsulation key, expanded from the seed. */
  secretKey: Uint8Array;
}

/**
 * Whether this build makes and uses an ML-KEM key at all. Always, in the
 * app; tests from before M78 switch it off to run as an older device.
 */
export function pqAvailable(): boolean {
  return true;
}

/** A key pair from a 64-byte seed (or a fresh one). */
export function mlkemKeyPair(seed?: Uint8Array): MlkemKeyPair {
  const d =
    seed ?? globalThis.crypto.getRandomValues(new Uint8Array(MLKEM_SEED_BYTES));
  if (d.length !== MLKEM_SEED_BYTES) throw new Error('E2EE_MLKEM_SEED');
  const { publicKey, secretKey } = ml_kem768.keygen(d);
  return { seed: d, publicKey: toBase64(publicKey), secretKey };
}

/**
 * Is this a usable ML-KEM-768 public key (FIPS 203 §7.2: the length and
 * the modulus check)? A key that fails is treated as missing (§4.2).
 */
export function mlkemPublicKeyValid(ek: string | null | undefined): boolean {
  if (typeof ek !== 'string') return false;
  try {
    const bytes = fromBase64(ek);
    if (bytes.length !== PQ.MLKEM_PUBLIC_KEY_BYTES) return false;
    // noble checks the modulus on encapsulation; a throwaway one tells.
    ml_kem768.encapsulate(bytes);
    return true;
  } catch {
    return false;
  }
}

export type WrapKind = 'chat' | 'family';

export interface PqWrapScope {
  kind: WrapKind;
  scopeId: string;
  epoch: number;
}

/** A field that can't go into `info`: refused before deriving (D337). */
export class PqScopeError extends Error {
  constructor() {
    super('E2EE_WRAP_SCOPE');
  }
}

function checkScope(scope: PqWrapScope) {
  if (scope.kind !== 'chat' && scope.kind !== 'family') {
    throw new PqScopeError();
  }
  if (typeof scope.scopeId !== 'string' || scope.scopeId.length === 0) {
    throw new PqScopeError();
  }
  if (
    !Number.isInteger(scope.epoch) ||
    scope.epoch < 0 ||
    scope.epoch > U32_MAX
  ) {
    throw new PqScopeError();
  }
}

/** th = SHA-256(C("wrap-th", [spki_W, spki_R, ek_R, ct])) (§5). */
export async function transcriptHash(
  wrapperSpki: Uint8Array,
  recipientSpki: Uint8Array,
  recipientEk: Uint8Array,
  ct: Uint8Array
): Promise<Uint8Array<ArrayBuffer>> {
  return new Uint8Array(
    await subtle().digest(
      'SHA-256',
      aadV2('wrap-th', [wrapperSpki, recipientSpki, recipientEk, ct])
    )
  );
}

/** info = C("wrap", [kind, lower(scopeId), epoch:u32, th]) (§5). */
export function wrapInfo(
  scope: PqWrapScope,
  th: Uint8Array
): Uint8Array<ArrayBuffer> {
  checkScope(scope);
  return aadV2('wrap', [
    scope.kind,
    scope.scopeId.toLowerCase(),
    scope.epoch,
    th,
  ]);
}

async function ecdhSecret(
  myPrivate: CryptoKey,
  theirSpki: Uint8Array<ArrayBuffer>
): Promise<Uint8Array<ArrayBuffer>> {
  const theirs = await subtle().importKey(
    'spki',
    theirSpki,
    { name: 'ECDH', namedCurve: 'P-256' },
    false,
    []
  );
  return new Uint8Array(
    await subtle().deriveBits({ name: 'ECDH', public: theirs }, myPrivate, 256)
  );
}

function concat(...parts: Uint8Array[]): Uint8Array<ArrayBuffer> {
  const out = new Uint8Array(
    new ArrayBuffer(parts.reduce((n, p) => n + p.length, 0))
  );
  let at = 0;
  for (const part of parts) {
    out.set(part, at);
    at += part.length;
  }
  return out;
}

async function deriveWrapKey(
  ikm: Uint8Array<ArrayBuffer>,
  info: Uint8Array<ArrayBuffer>,
  usage: 'encrypt' | 'decrypt'
): Promise<CryptoKey> {
  const base = await subtle().importKey('raw', ikm, 'HKDF', false, [
    'deriveKey',
  ]);
  return subtle().deriveKey(
    { name: 'HKDF', hash: 'SHA-256', salt: SALT, info },
    base,
    { name: 'AES-GCM', length: 256 },
    false,
    [usage]
  );
}

/** The wrap's intermediates, for the shared vector. */
export interface WrapTrace {
  ecdh: Uint8Array;
  cipherText: Uint8Array;
  sharedSecret: Uint8Array;
  th: Uint8Array;
  info: Uint8Array;
  ikm: Uint8Array;
  wrapped: string;
}

/**
 * Wraps a raw content key for one device, v2 (D337, §5). `fixed` (the
 * encapsulation's `m` and the IV) is for the shared vector only.
 */
export async function wrapV2(input: {
  rawContentKey: Uint8Array<ArrayBuffer>;
  wrapperPrivate: CryptoKey;
  wrapperSpki: string;
  recipientSpki: string;
  /** The recipient's ML-KEM key as recorded (D338), base64. */
  recipientEk: string;
  scope: PqWrapScope;
  fixed?: { m: Uint8Array; iv: Uint8Array<ArrayBuffer> };
}): Promise<WrapTrace> {
  checkScope(input.scope);
  if (input.rawContentKey.length !== 32) throw new Error('E2EE_WRAP_KEY');
  const ek = fromBase64(input.recipientEk);
  if (ek.length !== PQ.MLKEM_PUBLIC_KEY_BYTES)
    throw new Error('E2EE_MLKEM_KEY');
  const recipientSpki = fromBase64(input.recipientSpki);
  // noble runs FIPS 203's modulus check on the key here (§4.2).
  const { cipherText, sharedSecret } = ml_kem768.encapsulate(
    ek,
    input.fixed?.m
  );
  const ecdh = await ecdhSecret(input.wrapperPrivate, recipientSpki);
  const th = await transcriptHash(
    fromBase64(input.wrapperSpki),
    recipientSpki,
    ek,
    cipherText
  );
  const info = wrapInfo(input.scope, th);
  const ikm = concat(ecdh, sharedSecret);
  const key = await deriveWrapKey(ikm, info, 'encrypt');
  const iv =
    input.fixed?.iv ??
    globalThis.crypto.getRandomValues(
      new Uint8Array(new ArrayBuffer(IV_BYTES))
    );
  const body = new Uint8Array(
    await subtle().encrypt(
      { name: 'AES-GCM', iv, additionalData: Uint8Array.of(VERSION) },
      key,
      input.rawContentKey
    )
  );
  const wrapped = toBase64(
    concat(Uint8Array.of(VERSION), cipherText, iv, body)
  );
  return { ecdh, cipherText, sharedSecret, th, info, ikm, wrapped };
}

/** A content key wrapped for one device, v2: base64 (D337). */
export async function wrapContentKeyV2(
  contentKey: CryptoKey,
  wrapperPrivate: CryptoKey,
  wrapperSpki: string,
  recipientSpki: string,
  recipientEk: string,
  scope: PqWrapScope
): Promise<string> {
  const raw = new Uint8Array(await subtle().exportKey('raw', contentKey));
  return (
    await wrapV2({
      rawContentKey: raw,
      wrapperPrivate,
      wrapperSpki,
      recipientSpki,
      recipientEk,
      scope,
    })
  ).wrapped;
}

/** Opening a v2 copy failed: locked, never a wrong key (§4.3). */
export class PqUnwrapError extends Error {
  constructor(readonly reason: string) {
    super(reason);
  }
}

/**
 * Opens a v2 copy with this device's keys (§5 "Opening"): the length and
 * version byte, decapsulate, ECDH with the wrapper's key (already checked
 * against the record, D301), th from the wrapper's key and this device's
 * own two public keys, derive, decrypt with aad 02; 32 bytes or nothing.
 */
export async function unwrapRawV2(input: {
  wrapped: string;
  myPrivate: CryptoKey;
  mySpki: string;
  myMlkem: Pick<MlkemKeyPair, 'publicKey' | 'secretKey'>;
  wrapperSpki: string;
  scope: PqWrapScope;
}): Promise<Uint8Array<ArrayBuffer>> {
  checkScope(input.scope);
  if (wrappedKeyFormat(input.wrapped) !== 'v2') {
    throw new PqUnwrapError('E2EE_WRAP_FORMAT');
  }
  const bytes = fromBase64(input.wrapped);
  const ct = bytes.subarray(1, 1 + PQ.MLKEM_CIPHERTEXT_BYTES);
  const iv = bytes.slice(
    1 + PQ.MLKEM_CIPHERTEXT_BYTES,
    1 + PQ.MLKEM_CIPHERTEXT_BYTES + IV_BYTES
  );
  const body = bytes.slice(1 + PQ.MLKEM_CIPHERTEXT_BYTES + IV_BYTES);
  // Implicit rejection: a copy made for another key gives an unrelated
  // secret here, and the GCM tag fails below.
  const ss = ml_kem768.decapsulate(ct, input.myMlkem.secretKey);
  const ecdh = await ecdhSecret(input.myPrivate, fromBase64(input.wrapperSpki));
  const th = await transcriptHash(
    fromBase64(input.wrapperSpki),
    fromBase64(input.mySpki),
    fromBase64(input.myMlkem.publicKey),
    ct
  );
  const key = await deriveWrapKey(
    concat(ecdh, ss),
    wrapInfo(input.scope, th),
    'decrypt'
  );
  let raw: Uint8Array<ArrayBuffer>;
  try {
    raw = new Uint8Array(
      await subtle().decrypt(
        { name: 'AES-GCM', iv, additionalData: Uint8Array.of(VERSION) },
        key,
        body
      )
    );
  } catch {
    throw new PqUnwrapError('E2EE_WRAP_OPEN');
  }
  if (raw.length !== 32) throw new PqUnwrapError('E2EE_WRAP_KEY_LENGTH');
  return raw;
}

/** As `unwrapRawV2`, imported as a content key that can be passed on (D57). */
export async function unwrapContentKeyV2(
  input: Parameters<typeof unwrapRawV2>[0]
): Promise<CryptoKey> {
  const raw = await unwrapRawV2(input);
  return subtle().importKey('raw', raw, 'AES-GCM', true, [
    'encrypt',
    'decrypt',
  ]);
}

// ---- The decisions (D338, D339), as the vector states them ----------------

export type PqCount = 'e2ee_pq_key_missing' | 'e2ee_pq_key_changed' | null;

/**
 * What to wrap for one target device (D339, §6.2, vector `senderCases`):
 * `recorded` is the record's ML-KEM key for it, `answer` this answer's.
 */
export function pqSenderDecision(input: {
  recorded: string | null;
  answer: string | null;
  graceOver: boolean;
}): {
  format: 'v2' | 'v1' | 'skip';
  key: 'recorded' | 'answer' | null;
  learn?: true;
  count: PqCount;
} {
  const { recorded, answer, graceOver } = input;
  if (recorded !== null) {
    return {
      format: 'v2',
      key: 'recorded',
      count:
        answer === null
          ? 'e2ee_pq_key_missing'
          : answer !== recorded
            ? 'e2ee_pq_key_changed'
            : null,
    };
  }
  if (answer !== null) {
    return { format: 'v2', key: 'answer', learn: true, count: null };
  }
  return graceOver
    ? { format: 'skip', key: null, count: null }
    : { format: 'v1', key: null, count: null };
}

export type PqLearn =
  | 'learn-silently'
  | 'same'
  | 'keep-recorded-changed'
  | 'keep-recorded-missing'
  | 'none'
  | 'ignore'
  | 'new-device-with-key'
  | 'new-device';

/**
 * What the record does with a listed device's ML-KEM key (D338, §6.1,
 * vector `learnCases`). `p256`: the listed P-256 key is recorded and
 * current, recorded but removed, or not recorded.
 */
export function pqLearnDecision(input: {
  p256: 'current' | 'removed' | 'none';
  recordedMlkem: string | null;
  answerMlkem: string | null;
}): PqLearn {
  const { p256, recordedMlkem, answerMlkem } = input;
  if (p256 === 'none') {
    return answerMlkem !== null ? 'new-device-with-key' : 'new-device';
  }
  if (p256 === 'removed') return 'ignore';
  if (recordedMlkem === null) {
    return answerMlkem !== null ? 'learn-silently' : 'none';
  }
  if (answerMlkem === recordedMlkem) return 'same';
  return answerMlkem === null
    ? 'keep-recorded-missing'
    : 'keep-recorded-changed';
}

/**
 * Whether to open a v1 copy (D339, §6.2, vector `v1Cases`): always when
 * this device has no ML-KEM key or the record shows none for the wrapper;
 * otherwise only at or below this device's PQ ceiling for the scope.
 */
export function v1CopyDecision(input: {
  iHaveMlkem: boolean;
  wrapperHasMlkem: boolean;
  epoch: number;
  ceiling: number | null;
}): 'open' | 'refuse' {
  if (!input.iHaveMlkem || !input.wrapperHasMlkem) return 'open';
  // Ceilings are written for every scope at acceptance; a scope without
  // one (joined since, or registered with the key) is 0 (review M4).
  return input.epoch <= (input.ceiling ?? 0) ? 'open' : 'refuse';
}

/**
 * A scope's PQ ceiling at acceptance (D339, vector `ceilingCases`): the
 * server's `held`, capped at the higher of the highest epoch this device
 * had already opened there and the highest whose listed copy it opens now.
 * Never the server's number alone (final review, point 4).
 */
export function acceptanceCeiling(input: {
  held: number;
  openedLocally: number | null;
  opensNow: number | null;
}): number {
  return Math.min(
    input.held,
    Math.max(input.openedLocally ?? 0, input.opensNow ?? 0)
  );
}

/**
 * The backstop rotation (D339, §6.3, vector `rotateCases`): a device with
 * an ML-KEM key whose own copy of the current epoch is v1 starts a new
 * epoch before its next send, once per scope.
 */
export function pqShouldRotate(input: {
  iHaveMlkem: boolean;
  myCurrentCopy: 'v1' | 'v2' | null;
  alreadyRotated: boolean;
}): boolean {
  return (
    input.iHaveMlkem && input.myCurrentCopy === 'v1' && !input.alreadyRotated
  );
}

/**
 * A device's line in a security code (D338, D63): its P-256 key, or
 * `<P-256 key> <ML-KEM key>` once it has one.
 */
export function securityCodeLine(device: {
  publicKey: string;
  mlkem?: string | null;
}): string {
  return device.mlkem
    ? `${device.publicKey} ${device.mlkem}`
    : device.publicKey;
}
